import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { MessageSquare, Phone, FileDown, Search, Loader2 } from "lucide-react";
import axios from "axios";

const API = getApiUrl();

const PERIOD_OPTIONS = [
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
];

const GOAL_META = {
  enquiry_submit: { icon: MessageSquare, color: "#8b5cf6", label: "Enquiries" },
  call_click: { icon: Phone, color: "#f59e0b", label: "Call Clicks" },
  brochure_download: { icon: FileDown, color: "#3b82f6", label: "Brochure Downloads" },
  search: { icon: Search, color: "#10b981", label: "Searches" },
};

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border shadow-md p-2 rounded text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
}

export default function AnalyticsGoals({ period, onPeriodChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState("bar");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/analytics/goals?period=${period}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const goals = data?.goals || [];
  const chartData = (data?.chart_data || []).map((d) => ({ ...d, label: formatDate(d.date) }));
  const tickInterval = chartData.length > 14 ? Math.floor(chartData.length / 7) : 0;

  return (
    <div className="space-y-5" data-testid="analytics-goals">
      {/* Period toggle + chart type toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              onClick={() => onPeriodChange(p.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                period === p.value ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {["bar", "line"].map((t) => (
            <button
              key={t}
              onClick={() => setChartType(t)}
              className={`px-2.5 py-1 text-xs rounded-full capitalize transition-colors ${
                chartType === t ? "bg-primary text-white" : "bg-muted text-muted-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Goal totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {goals.map((goal, i) => {
          const meta = GOAL_META[goal.key] || {};
          const Icon = meta.icon || MessageSquare;
          return (
            <div key={i} className="bg-white border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${meta.color}20` }}>
                  <Icon className="h-4 w-4" style={{ color: meta.color }} />
                </div>
                <span className="text-xs text-muted-foreground">{goal.name}</span>
              </div>
              <p className="text-2xl font-bold font-serif">{goal.count.toLocaleString()}</p>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <div className="bg-white border border-border rounded-lg p-5">
        <h3 className="font-serif text-sm font-medium mb-4">Goal Completions Over Time</h3>
        <ResponsiveContainer width="100%" height={260}>
          {chartType === "bar" ? (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickInterval} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="enquiries" name="Enquiries" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
              <Bar dataKey="calls" name="Calls" fill="#f59e0b" radius={[2, 2, 0, 0]} />
              <Bar dataKey="downloads" name="Downloads" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              <Bar dataKey="searches" name="Searches" fill="#10b981" radius={[2, 2, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickInterval} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="enquiries" name="Enquiries" stroke="#8b5cf6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="calls" name="Calls" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="downloads" name="Downloads" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="searches" name="Searches" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Goal performance table */}
      <div className="bg-white border border-border rounded-lg p-5">
        <h3 className="font-serif text-sm font-medium mb-3">Goal Performance Summary</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 text-muted-foreground font-normal">Goal</th>
              <th className="text-right py-2 text-muted-foreground font-normal">Total</th>
              <th className="text-right py-2 text-muted-foreground font-normal">Daily Avg</th>
            </tr>
          </thead>
          <tbody>
            {goals.map((goal, i) => {
              const days = chartData.length || 1;
              const meta = GOAL_META[goal.key] || {};
              const Icon = meta.icon || MessageSquare;
              return (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                      <span>{goal.name}</span>
                    </div>
                  </td>
                  <td className="text-right py-2.5 font-bold">{goal.count.toLocaleString()}</td>
                  <td className="text-right py-2.5 text-muted-foreground">
                    {(goal.count / days).toFixed(1)}/day
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
